import { apiClient } from './apiClient';
import { Space } from '../types/auth';
import { Logger } from '../utils/logger';

// V4-FRONT-ANY-USAGE batch 6 2026-05-29 — DTO snake_case du backend /spaces
interface BackendSpace {
  id: string;
  name: string;
  slug?: string;
  owner_id: string;
  logo_url?: string;
  created_at: string;
  agency_id?: string | null;
  space_role?: Space['spaceRole'];
}

export const spaceService = {
  async getMySpaces(): Promise<Space[]> {
    Logger.debug('[SpaceService] Fetching user spaces');
    const response = await apiClient.get<BackendSpace[]>('/spaces/');
    const spaces = response.data.map(space => ({
      id: space.id,
      name: space.name,
      slug: space.slug || space.name.toLowerCase().replace(/\s+/g, '-'),
      ownerId: space.owner_id,
      logoUrl: space.logo_url,
      createdAt: new Date(space.created_at),
      agencyId: space.agency_id || undefined,
      spaceRole: space.space_role || undefined,
    }));
    Logger.info('[SpaceService] User spaces fetched', { count: spaces.length });
    return spaces;
  },

  async createSpace(name: string): Promise<Space> {
    Logger.info('[SpaceService] Creating space', { name });
    const response = await apiClient.post<BackendSpace>('/spaces', { name });
    const space = response.data;
    const createdSpace = {
      id: space.id,
      name: space.name,
      slug: space.slug || space.name.toLowerCase().replace(/\s+/g, '-'),
      ownerId: space.owner_id,
      logoUrl: space.logo_url,
      createdAt: new Date(space.created_at),
    };
    Logger.info('[SpaceService] Space created', { spaceId: createdSpace.id, name: createdSpace.name });
    return createdSpace;
  },
  
  async updateSpace(id: string, data: Partial<Space>): Promise<Space> {
    Logger.info('[SpaceService] Updating space', {
      spaceId: id,
      hasName: !!data.name,
      hasLogoUrl: !!data.logoUrl,
    });
    // Map frontend camelCase to backend snake_case
    // V4-FRONT-ANY-USAGE-BATCH-3 2026-05-28 — typage explicite (PATCH partiel).
    const updateData: { name?: string; logo_url?: string } = {};
    if (data.name) updateData.name = data.name;
    if (data.logoUrl) updateData.logo_url = data.logoUrl;
    
    const response = await apiClient.put<BackendSpace>(`/spaces/${id}`, updateData);
    const space = response.data;
    const updatedSpace = {
      id: space.id,
      name: space.name,
      slug: space.slug || space.name.toLowerCase().replace(/\s+/g, '-'),
      ownerId: space.owner_id,
      logoUrl: space.logo_url,
      createdAt: new Date(space.created_at),
    };
    Logger.info('[SpaceService] Space updated', { spaceId: updatedSpace.id, name: updatedSpace.name });
    return updatedSpace;
  },

  async uploadLogo(spaceId: string, file: File): Promise<Space> {
    Logger.info('[SpaceService] Uploading space logo', {
      spaceId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    });
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post<BackendSpace>(`/spaces/${spaceId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    const space = response.data;
    const updatedSpace = {
      id: space.id,
      name: space.name,
      slug: space.slug || space.name.toLowerCase().replace(/\s+/g, '-'),
      ownerId: space.owner_id,
      logoUrl: space.logo_url,
      createdAt: new Date(space.created_at),
    };
    Logger.info('[SpaceService] Space logo uploaded', { spaceId: updatedSpace.id, hasLogoUrl: !!updatedSpace.logoUrl });
    return updatedSpace;
  }
};
